/** Validation regex + caller-set SSOT constants: the canonical validation patterns (kebab-case, safe-identifier,
 * email/phone extraction, numeric, SQL column-name) and the `VALID_CALLERS_*` allow-set arrays consumed by
 * caller-authorization checks. Pure-leaf module: NO value imports.
 */

import type { CallerType } from '@federa/types/adapters-caller-context';

export const KEBAB_CASE_PATTERN = /^[a-z][a-z0-9-]*$/;

/**
 * Kebab slug whose FIRST character may also be a digit -- the repo's slug token shape wherever a leading digit is
 * legal (a `flows/<id>.ts` leaf id, a `.claude/skills/<slug>` directory name). A distinct DECISION from
 * {@link KEBAB_CASE_PATTERN} above, which is letter-led, so the two stay separate members rather than one widened
 * pattern: relaxing the letter-led form would silently admit digit-led values everywhere it gates.
 *
 * Shared as a COMPILED instance: flagless, and every consumer drives it with `.test()`, which touches `lastIndex`
 * only under `/g` or `/y`. Paired forbid-rule for this SSOT class: `QUAL-CROSS-FILE-REGEX-CONST-DUP-001`, which fires
 * once a regex source and flags are spelled byte-for-byte in two or more files -- the single-file re-spelling is a
 * recorded, accepted gap of that rule.
 */
export const KEBAB_DIGIT_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]*$/;

export const SAFE_IDENTIFIER_PATTERN = /^[a-zA-Z0-9_-]+$/;

export const EMAIL_EXTRACTION_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi;

export const IS_NUMERIC = /^\d+$/;

/**
 * A POSITIVE whole number spelled in ASCII digits -- no sign, no fraction, no leading zero, never zero: the shape git
 * prints an epoch instant in (`const/cli-git.ts`) and a standard chain's `-r<N>` revision carries
 * (`const/standard-chain.ts`). {@link IS_NUMERIC} admits `0` and `007`, so the two stay separate members. Flagless and
 * compiled, every consumer drives it with `.test()`.
 */
export const POSITIVE_INTEGER_DIGITS_PATTERN = /^[1-9]\d*$/;

const WITH_EXTERNAL = ['admin', 'user', 'system', 'external', 'flow'] as const satisfies readonly CallerType[];

const SYSTEM_ONLY = ['admin', 'system'] as const satisfies readonly CallerType[];

const KAIZEN_EXTENDED = ['admin', 'system', 'kaizen-flow', 'flow', 'flow-engine'] as const satisfies readonly CallerType[];

/** The three caller allow-sets, grouped per the `HTTP_STATUS` pattern (QUAL-CONST-GROUP-001). */
export const VALID_CALLERS = {
	WITH_EXTERNAL,
	SYSTEM_ONLY,
	KAIZEN_EXTENDED,
} as const;
